'use client';

import React, { useMemo, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Participant } from 'livekit-client';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useHostControl } from './HostControlContext';
import { resolveRoleFromMetadata } from './participantRoles';
import { useGlobalSpotlight, useGlobalSpotlightState } from './useGlobalSpotlight';
import { Crown, Mic, MicOff, MoreHorizontal, Video, VideoOff, Star, StarOff } from 'lucide-react';
import { cn } from '@/lib/utils';

interface HostControlsProps {
  participant: Participant;
  isLocal: boolean;
  displayName: string;
  hasAudio: boolean;
  hasVideo: boolean;
  onRequestAudio: () => Promise<void>;
  onMute: () => Promise<void>;
  onDisableVideo: () => Promise<void>;
  variant?: 'default' | 'dropdown-only';
  dropdownTriggerClassName?: string;
  onMenuOpenChange?: (open: boolean) => void;
}

interface ParticipantActionMenuProps {
  displayName: string;
  participantIdentity: string;
  canManageRole: boolean;
  isParticipantCoHost: boolean;
  showRequestAudio: boolean;
  showMute: boolean;
  showDisableVideo: boolean;
  onRequestAudio: () => Promise<void>;
  onMute: () => Promise<void>;
  onDisableVideo: () => Promise<void>;
  onToggleCoHost: () => Promise<void>;
  triggerClassName?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function HostControls({
  participant,
  isLocal,
  displayName,
  hasAudio,
  hasVideo,
  onRequestAudio,
  onMute,
  onDisableVideo,
  variant = 'default',
  dropdownTriggerClassName,
  onMenuOpenChange,
}: HostControlsProps) {
  const { isHost, hasModeratorPermissions, assignCoHost, revokeCoHost } = useHostControl();

  const [menuOpen, setMenuOpen] = useState(false);

  const handleMenuOpenChange = useCallback(
    (open: boolean) => {
      setMenuOpen(open);
      onMenuOpenChange?.(open);
    },
    [onMenuOpenChange],
  );

  // Only show controls for moderators and not for local participant
  if (!hasModeratorPermissions || isLocal) return null;

  const participantRole = resolveRoleFromMetadata(participant.metadata);
  const isParticipantCoHost = participantRole === 'co-host';
  const isParticipantHost = participantRole === 'host';

  const handleToggleCoHost = async () => {
    if (!isHost || isParticipantHost) return;

    const success = isParticipantCoHost
      ? await revokeCoHost(participant.identity)
      : await assignCoHost(participant.identity);

    if (success) {
      if (isParticipantCoHost) {
        toast.success('Co-anfitrião removido.');
      } else {
        toast.success('Co-anfitrião designado com sucesso.');
      }
    }
  };

  const showRequestAudio = !hasAudio && !hasVideo;
  const showMute = hasAudio;
  const showDisableVideo = hasVideo;
  const canManageRole = isHost && !isParticipantHost;
  const menu = (
    <ParticipantActionMenu
      displayName={displayName}
      participantIdentity={participant.identity}
      canManageRole={canManageRole}
      isParticipantCoHost={isParticipantCoHost}
      showRequestAudio={showRequestAudio}
      showMute={showMute}
      showDisableVideo={showDisableVideo}
      onRequestAudio={onRequestAudio}
      onMute={onMute}
      onDisableVideo={onDisableVideo}
      onToggleCoHost={handleToggleCoHost}
      triggerClassName={dropdownTriggerClassName}
      open={menuOpen}
      onOpenChange={handleMenuOpenChange}
    />
  );

  if (variant === 'dropdown-only') {
    return menu;
  }

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-3">
        <div
          className={`p-1.5 rounded-full ${hasAudio ? 'bg-green-600' : 'bg-red-600'}`}
          title={hasAudio ? 'Microfone ativo' : 'Microfone desligado'}
        >
          {hasAudio ? (
            <Mic className="w-3 h-3 text-white" />
          ) : (
            <MicOff className="w-3 h-3 text-white" />
          )}
        </div>
        <div
          className={`p-1.5 rounded-full ${hasVideo ? 'bg-green-600' : 'bg-red-600'}`}
          title={hasVideo ? 'Câmera ativa' : 'Câmera desligada'}
        >
          {hasVideo ? (
            <Video className="w-3 h-3 text-white" />
          ) : (
            <VideoOff className="w-3 h-3 text-white" />
          )}
        </div>
      </div>

      {menu}
    </div>
  );
}

export function ParticipantActionMenu({
  displayName,
  participantIdentity,
  canManageRole,
  isParticipantCoHost,
  showRequestAudio,
  showMute,
  showDisableVideo,
  onRequestAudio,
  onMute,
  onDisableVideo,
  onToggleCoHost,
  triggerClassName,
  open,
  onOpenChange,
}: ParticipantActionMenuProps) {
  const { toggleGlobalSpotlight, canControlSpotlight } = useGlobalSpotlight();
  const { globalSpotlightTarget } = useGlobalSpotlightState();
  const { isHost, hasModeratorPermissions } = useHostControl();
  const isCurrentlySpotlighted = globalSpotlightTarget === participantIdentity;

  const menuLabels = useMemo(() => {
    return {
      requestAudio: 'Pedir para ativar som',
      mute: `Silenciar ${displayName}`,
      disableVideo: `Parar vídeo de ${displayName}`,
      assign: 'Designar como Co-Anfitrião',
      revoke: 'Remover de Co-Anfitrião',
      spotlight: `Destacar ${displayName} para todos`,
      removeSpotlight: 'Remover destaque',
    };
  }, [displayName]);

  const handleSpotlight = async () => {
    await toggleGlobalSpotlight(participantIdentity);
  };

  const handleRemoveSpotlight = async () => {
    await toggleGlobalSpotlight(null);
  };

  const handleMenuAction = async (action: () => Promise<void> | void) => {
    try {
      await action();
    } catch (error) {
      console.error('Erro ao executar ação do menu de participante:', error);
      toast.error('Não foi possível completar a ação.');
    }
  };

  const hasPrimaryActions = showMute || showDisableVideo;
  const showSpotlight = canControlSpotlight;
  const showRemoveSpotlight = canControlSpotlight && globalSpotlightTarget !== null;
  const hasAnyAction = canManageRole || hasPrimaryActions || showSpotlight;

  if (!hasAnyAction) {
    return null;
  }

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          size={triggerClassName ? 'sm' : 'icon'}
          variant={triggerClassName ? 'outline' : 'ghost'}
          className={cn(
            'border border-transparent transition-transform duration-150 focus-visible:ring-white/70 focus-visible:ring-offset-0 focus-visible:ring-2 data-[state=open]:translate-y-0',
            triggerClassName ??
              'h-8 w-8 text-white hover:text-white bg-neutral-800/60 hover:bg-neutral-700',
          )}
          title="Ações do participante"
        >
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-60 bg-neutral-800 text-white border-neutral-700">
        {showSpotlight && !isCurrentlySpotlighted && (
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault();
              handleMenuAction(handleSpotlight);
            }}
            className="flex items-center gap-2 focus:bg-neutral-700"
          >
            <Star className="w-4 h-4" />
            <span>{menuLabels.spotlight}</span>
          </DropdownMenuItem>
        )}

        {showRemoveSpotlight && isCurrentlySpotlighted && (
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault();
              handleMenuAction(handleRemoveSpotlight);
            }}
            className="flex items-center gap-2 focus:bg-neutral-700"
          >
            <StarOff className="w-4 h-4" />
            <span>{menuLabels.removeSpotlight}</span>
          </DropdownMenuItem>
        )}

        {(showSpotlight || showRemoveSpotlight) && (canManageRole || hasPrimaryActions) && (
          <DropdownMenuSeparator className="bg-neutral-700" />
        )}

        {canManageRole && (
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault();
              handleMenuAction(onToggleCoHost);
            }}
            className="flex items-center gap-2 focus:bg-neutral-700"
          >
            <Crown className="w-4 h-4" />
            <span>{isParticipantCoHost ? menuLabels.revoke : menuLabels.assign}</span>
          </DropdownMenuItem>
        )}

        {canManageRole && hasPrimaryActions && <DropdownMenuSeparator className="bg-neutral-700" />}

        {showMute && (
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault();
              handleMenuAction(onMute);
            }}
            className="focus:bg-neutral-700"
          >
            {menuLabels.mute}
          </DropdownMenuItem>
        )}

        {showDisableVideo && (
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault();
              handleMenuAction(onDisableVideo);
            }}
            className="focus:bg-neutral-700"
          >
            {menuLabels.disableVideo}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
